import { hasTx, tx } from './i18n';

// Laboratory reference text comes from the translation table like every other
// text. A key is built from the row's own code, the same way the backend builds
// it (Modules/laboratory/ReferenceText.js), so nothing here holds a label.
const slug = value => String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
const ref = (...parts) => `ui.lab.ref.${parts.map(slug).join('.')}`;

export const departmentName = code => tx(ref('department', code));
export const departmentShort = code => tx(ref('department_short', code));
export const analysisName = code => tx(ref('analysis', code));
export const workflowName = code => tx(ref('workflow', code));
export const sampleTypeName = code => tx(ref('sample_type', code));
// One of our own sample types reads as the laboratory's kind it is tied to. A
// type tied to none (the result data types) keeps its own name.
export function ownSampleTypeName(type) {
  return typeof type.labCode === 'string' && hasTx(ref('sample_type', type.labCode)) ? sampleTypeName(type.labCode) : type.name;
}
export const stageName = code => tx(ref('stage', code));
export const fieldLabel = code => tx(ref('field', code));
export const categoryName = code => tx(ref('category', code));
export const parameterName = code => tx(ref('parameter', code));
export const criterionText = (ruleSetCode, position) => tx(ref('criterion', ruleSetCode, position));
export const noteText = (documentCode, noteNo) => tx(ref('note', documentCode, noteNo));

// A kit, reagent or platform keeps its own name in every language. A word (a
// method, a result, a problem) has a key.
export function itemLabel(item) {
  const key = ref('item', item.code);
  return hasTx(key) ? tx(key) : item.label;
}

// µL and mg read the same everywhere; "slices" and "tubes" are words.
export function unitSymbol(unit) {
  if (!unit) return '';
  const key = ref('unit', unit.code);
  return hasTx(key) ? tx(key) : unit.symbol;
}

const NUMBER = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const SUPERSCRIPT = '⁰¹²³⁴⁵⁶⁷⁸⁹';

// A cell count reads as the lab writes it: 10⁶, not 1,000,000.
export function labNumber(value) {
  const power = Math.log10(value);
  if (value >= 100000 && Number.isInteger(power)) return `10${String(power).split('').map(digit => SUPERSCRIPT[digit]).join('')}`;
  return NUMBER.format(value);
}

export function labAmount(min, max, unit) {
  const symbol = unitSymbol(unit);
  const text = min !== null && max !== null && max !== min
    ? tx('ui.lab.amount.range', { min: labNumber(min), max: labNumber(max), unit: symbol })
    : tx('ui.lab.amount.value', { value: labNumber(min !== null ? min : max), unit: symbol });
  return text.trim();
}

export const labTemperature = value => tx('ui.lab.amount.temperature', { value: value > 0 ? `+${value}` : String(value) });

// One line of the analysis catalogue. A requirement with a number is a named
// figure; one without is the sentence stored for that row.
export function requirementView(entry, requirement) {
  if (requirement.valueMin === null && requirement.valueMax === null) {
    return { key: requirement.position, sentence: tx(ref('requirement', entry.document, entry.listNo, requirement.position)) };
  }
  const condition = ref('condition', entry.document, entry.listNo, requirement.position);
  // "Tubes: 3 tubes" says it twice, so a unit that only repeats its label is left out.
  const counted = requirement.unit !== null && requirement.unit.dimension === requirement.parameter && hasTx(ref('unit', requirement.unit.code));
  return {
    key: requirement.position,
    label: parameterName(requirement.parameter),
    value: tx(`ui.lab.req.qualifier.${requirement.qualifier}`, { value: labAmount(requirement.valueMin, requirement.valueMax, counted ? null : requirement.unit) }),
    condition: hasTx(condition) ? tx(condition) : null,
    sampleTypeCode: requirement.sampleTypeCode,
  };
}

// The numbers a criterion carries, as short figures.
export function criterionLimits(criterion) {
  const limits = [];
  if (criterion.amountMin !== null) {
    limits.push({ kind: 'amount', label: tx(criterion.amountMax === null ? 'ui.lab.limit.minimum' : 'ui.lab.limit.amount'),
      value: labAmount(criterion.amountMin, criterion.amountMax, criterion.amountUnit) });
  }
  if (criterion.aliquotsMin !== null) {
    limits.push({ kind: 'aliquots', label: tx('ui.lab.limit.aliquots'),
      value: labAmount(criterion.aliquotsMin, criterion.aliquotsMax, null) });
  }
  if (criterion.temperatureMaxC !== null) {
    limits.push({ kind: 'temperature', label: tx('ui.lab.limit.warmest'), value: labTemperature(criterion.temperatureMaxC) });
  }
  return limits;
}
